import { useAppDispatch, useAppSelector } from "./../../../../hooks";
import { useEffect, useState } from "react";

import {
  Button,
  EmptyState,
  Loading,
  SecureCopy,
  SimpleTable,
  Typography,
} from "djuno-design";
import { useParams } from "react-router-dom";

import { Helmet } from "react-helmet";
import HighlighterText from "../../../general/HighlighterText";
import { useSearch } from "../../../../providers/SearchProvider";
import { SynchronisationModal } from "./SynchronisationModal";
import { AiVolume } from "../../../../types/ai";
import {
  getAiAppAsync,
  handleSynchronisation,
  selectAiApp,
  selectAiAppLoading,
} from "../../../../store/ai/deploys/appSlice";

const AiDeployAttachedDataTab = () => {
  const { appId } = useParams();
  const [filteredAiApps, setFilteredAiapps] = useState<AiVolume[]>([]);

  const dispatch = useAppDispatch();
  const { value: searchValue } = useSearch();

  const app = useAppSelector(selectAiApp);
  const appLoading = useAppSelector(selectAiAppLoading);

  console.log("filteredAiApps", filteredAiApps);

  useEffect(() => {
    if (!app) {
      if (appId) {
        dispatch(getAiAppAsync({ id: appId }));
      }
    }
  }, [dispatch, app, appId]);

  useEffect(() => {
    if (app) {
      setFilteredAiapps(app.spec.volumes);
    }
  }, [app]);

  if (appLoading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>AI Deploy</title>
        <meta name="description" content="" />
      </Helmet>

      <>
        <div className="flex flex-col gap-3">
          <Typography.Title level={5} className="!mb-0">
            Attach data containers
          </Typography.Title>

          <div className="items-center justify-between flex flex-1 transition duration-150">
            <Typography.Text className="!text-sm ">
              If necessary, you can attach Djuno cloud Object Storage containers
              to your notebook.Once they are attached, they will be temporarily
              loaded and cached near your instance, to minimise latency and
              improve performance. The best practice is to attach one container
              with incoming data, and another with your outgoing data.
            </Typography.Text>
          </div>

          <div className="flex items-center gap-2">
            <Button
              uiType="primary"
              onClick={() => dispatch(handleSynchronisation({ status: true }))}
              className="group"
            >
              Global data synchronisation
            </Button>
          </div>
        </div>

        <div className="mt-5 w-full">
          <SimpleTable loading={appLoading} containerClassName="min-h-[240px]">
            <SimpleTable.Head>
              <SimpleTable.Row>
                <SimpleTable.TH lable="Data container" />
                <SimpleTable.TH lable="Prefix" />
                <SimpleTable.TH lable="Location" />
                <SimpleTable.TH lable="Mount directory" />
                <SimpleTable.TH lable="Permission" />
                <SimpleTable.TH lable="Cache" />
              </SimpleTable.Row>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {app && filteredAiApps.length > 0 ? (
                filteredAiApps
                  .filter((c) => c.container || c.prefix)
                  .map((c, i) => (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.container}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.prefix}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.region}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.mountPath}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.permission}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.cache ? "true" : "false"}
                        </Typography.Text>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))
              ) : (
                <SimpleTable.Row withoutHoverStyle className="h-[200px]">
                  <SimpleTable.TD colSpan={6} className="!border-0">
                    <EmptyState text="No data available" />
                  </SimpleTable.TD>
                </SimpleTable.Row>
              )}
            </SimpleTable.Body>
          </SimpleTable>
        </div>

        <div className="flex flex-col gap-3">
          <Typography.Title level={5} className="!mb-0">
            Attached public Git repositorys
          </Typography.Title>
        </div>
        <div className="mt-5 w-full">
          <SimpleTable loading={appLoading} containerClassName="min-h-[240px]">
            <SimpleTable.Head>
              <SimpleTable.Row>
                <SimpleTable.TH lable="Git repository URL" />
                <SimpleTable.TH lable="Mount directory" />
                <SimpleTable.TH lable="Permission" />
                <SimpleTable.TH lable="Cache" />
              </SimpleTable.Row>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {app && filteredAiApps.length > 0 ? (
                filteredAiApps
                  .filter((c) => c.volumeSource?.publicGit?.url)
                  .map((c, i) => (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <HighlighterText
                          searchValue={searchValue}
                          textToHighlight={c.id || ""}
                          className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                        />
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.volumeSource?.publicGit?.url}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <SecureCopy type="copy" text={c.mountPath} />
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.permission || ""}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.cache ? "true" : "false"}
                        </Typography.Text>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))
              ) : (
                <SimpleTable.Row className="h-[200px]">
                  <SimpleTable.TD colSpan={4} className="!border-0">
                    <EmptyState text="No associated Git repository" />
                  </SimpleTable.TD>
                </SimpleTable.Row>
              )}
            </SimpleTable.Body>
          </SimpleTable>
        </div>

        <SynchronisationModal />
      </>
    </>
  );
};
export default AiDeployAttachedDataTab;
